<script setup lang="ts">
import { createFetch } from '@vueuse/core';
import { inject, provide, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { UAParser } from 'ua-parser-js';
import NavBar from '@/components/NavBar.vue';
import FooterBar from '@/components/FooterBar.vue';
import SiteNotification from '@/elements/SiteNotification.vue';
import RouteNotFoundView from '@/views/errors/RouteNotFoundView.vue';
import NotAuthenticatedView from '@/views/errors/NotAuthenticatedView.vue';
import { accountsTbProfileUrlKey, apiUrlKey, callKey, refreshKey, supportUrlKey, tbProUrlKey } from '@/keys';
import { StringResponse } from '@/models';
import { usePosthog, posthog } from '@/composables/posthog';
import { config } from '@/config';

// stores
import { useSiteNotificationStore } from '@/stores/alert-store';
import { useUserStore } from '@/stores/user-store';
import { createCalendarStore } from '@/stores/calendar-store';
import { createAppointmentStore } from '@/stores/appointment-store';
import { createScheduleStore } from '@/stores/schedule-store';
import { userManager } from '@/composables/oidcUserManager';
import { isOidcAuth } from '@/composables/authSchemes';
import NavBarMobile from './components/NavBarMobile.vue';

// component constants
const user = useUserStore();
const apiUrl = inject(apiUrlKey);
const route = useRoute();
const router = useRouter();

const siteNotificationStore = useSiteNotificationStore();
const {
  isVisible: visibleNotification,
  title: notificationTitle,
  actionUrl: notificationActionUrl,
  message: notificationMessage,
} = storeToRefs(siteNotificationStore);

provide(accountsTbProfileUrlKey, config.tbAccountDashboardUrl);
provide(tbProUrlKey, config.tbProUrl);
provide(supportUrlKey, config.supportUrl);

const { isSame: isSameNotification, show: showNotification, lock: lockNotification } = siteNotificationStore;

if (isOidcAuth) {
  /**
   * The user is reloaded after the access token is refreshed
   * So let's use this event to silently update our copy of the access token
   */
  userManager.events.addUserLoaded(async () => {
    const newAccessToken = (await userManager.getUser())?.access_token;
    console.debug('[userManager] User Loaded Evt, is new token the same?', newAccessToken === user.data.accessToken);
    user.data.accessToken = newAccessToken;
  });
}

// handle auth and fetch
const call = createFetch({
  baseUrl: apiUrl,
  options: {
    beforeFetch: async ({ options }) => {
      if (user?.authenticated) {
        const token = user.data.accessToken;
        // @ts-expect-error ignore headers type error
        options.headers.Authorization = `Bearer ${token}`;
      }
      return { options };
    },
    updateDataOnError: true, // Needed to access the actual error message...
    async onFetchError(context) {
      const { data, response } = context;
      // Catch any google refresh error that may occur
      if (data?.detail?.id === 'GOOGLE_REFRESH_ERROR' && !isSameNotification('GOOGLE_REFRESH_ERROR')) {
        // Ensure other async calls don't reach here
        lockNotification(data.detail.error);

        // Retrieve the google auth url, and if that fails send them to calendar settings!
        const { data: urlData, error: urlError }: StringResponse = await call('google/auth').get();
        const url = urlError.value ? '/settings/calendar' : (urlData.value as string).slice(1, -1);

        // Update our site notification store with the error details
        showNotification(
          data.detail.error,
          'Action needed!',
          data.detail?.message || 'Please re-connect with Google',
          url
        );
      } else if (response && response.status === 401 && data?.detail?.id === 'INVALID_TOKEN') {
        // Clear current user data, and ship them to the login screen!
        user.$reset();
        await router.push('/login');
        return context;
      }

      // Pass the error along
      return context;
    },
  },
  fetchOptions: {
    mode: 'cors',
    credentials: 'include',
  },
});

// Now that we created the call function, we can initialize API calls for the user store
user.init(call);

provide(callKey, call);

// menu items for main navigation
const navItems = ['dashboard', 'availability', 'bookings', 'settings'];

// db tables
const calendarStore = createCalendarStore(call);
const appointmentStore = createAppointmentStore(call);
const scheduleStore = createScheduleStore(call);

// true if route can be accessed without authentication
const routeIsPublic = computed(() => !!route.meta?.isPublic);
const routeIsHome = computed(() => ['home'].includes(typeof route.name === 'string' ? route.name : ''));
const routeIsLogin = computed(() => ['login'].includes(typeof route.name === 'string' ? route.name : ''));
const routeHasModal = computed(() => ['login'].includes(typeof route.name === 'string' ? route.name : ''));
const routeIsFTUE = computed(() => ['setup'].includes(typeof route.name === 'string' ? route.name : ''));
const routeIsPostLogin = computed(() => ['post-login'].includes(typeof route.name === 'string' ? route.name : ''));
const routeIsLogout = computed(() => ['logout'].includes(typeof route.name === 'string' ? route.name : ''));
const routeIsMaintenance = computed(() => ['maintenance'].includes(typeof route.name === 'string' ? route.name : ''));
// The router.ts file has a beforeEach guard that redirects to the OIDC login
// but we are flashing a NotFoundView briefly which is uh... not ideal
const routeNameUndefinedDueToAbortedNav = computed(() => !route.name && !user?.authenticated && isOidcAuth);

// retrieve calendars and appointments after checking login and persisting user to db
const getDbData = async () => {
  if (user?.authenticated) {
    await Promise.all([user.profile(), calendarStore.fetch(true), appointmentStore.fetch(), scheduleStore.fetch(true)]);
  }
};

const onPageLoad = async () => {
  /**
   * Metric collection for development purposes.
   * This data will be used to help guide development, design, and user experience decisions.
   */
  /*
  const parser = new UAParser(navigator.userAgent);
  const browser = parser.getBrowser();
  const os = parser.getOS();
  const device = parser.getDevice();
  const deviceRes = `${window?.screen?.width ?? -1}x${window?.screen?.height ?? -1}`;
  const effectiveDeviceRes = `${window?.screen?.availWidth ?? -1}x${window?.screen?.availHeight ?? -1}`;

  const response = await call('metrics/page-load').post({
    browser: browser.name,
    browser_version: `${browser.name}:${browser.version}`,
    os: os.name,
    os_version: `${os.name}:${os.version}`,
    device: device.model,
    device_model: `${device.vendor}:${device.model}`,
    resolution: deviceRes,
    effective_resolution: effectiveDeviceRes,
    user_agent: navigator.userAgent,
    locale: defaultLocale(), // needs to import defaultLocale from utils
  }).json();

  const { data } = response;
  return data.value?.id ?? false;
   */

  // Only used for non-subscribers
  const parser = new UAParser(navigator.userAgent);
  const browser = parser.getBrowser();
  const os = parser.getOS();

  const response = await call('metrics/get-id')
    .post({
      browser_version: `${browser.name}:${browser.version}`,
      os_version: `${os.name}:${os.version}`,
    })
    .json();
  const { data } = response;
  return data.value?.id ?? false;
};

// provide refresh functions for components
provide(refreshKey, getDbData);

onMounted(async () => {
  // Ensure we have a cookie available for POST/PUT/DELETEs
  await call('session-info').get();

  if (usePosthog) {
    const REMOVED_PROPERTY = '<removed>';
    const UNKNOWN_PROPERTY = '<unknown>';

    // Hack to clear $set_once until we get confirmation that this can be filtered.
    // Move the function reference so we can patch it and still retrieve the results before we sanitize it.

    /* eslint no-underscore-dangle: ["error", { "allow": ["_calculate_set_once_properties"] }] */

    if (posthog['_original_calculate_set_once_properties'] === undefined) {
      posthog['_original_calculate_set_once_properties'] = posthog._calculate_set_once_properties;
    }
    posthog._calculate_set_once_properties = function patch(dataSetOnce?) {
      dataSetOnce = posthog['_original_calculate_set_once_properties'](dataSetOnce);

      if (dataSetOnce?.$initial_current_url || dataSetOnce?.$initial_pathname) {
        dataSetOnce.$initial_current_url = REMOVED_PROPERTY;
        dataSetOnce.$initial_pathname = REMOVED_PROPERTY;
      }

      return dataSetOnce;
    };

    posthog.init(config.posthogProjectKey, {
      api_host: config.posthogHost,
      ui_host: config.posthogUiHost,
      person_profiles: 'identified_only',
      persistence: 'memory',
      mask_all_text: true,
      mask_all_element_attributes: true,
      autocapture: false, // Off for now until we can figure out $set_once.
      sanitize_properties: (properties, event) => {
        // If the route isn't available to use right now, ignore the capture.
        if (!route.name) {
          return {
            captureFailedMessage: 'route.name was not available.',
          };
        }

        // Do we need to mask the path?
        if (route.meta?.maskForMetrics) {
          // Replace recorded path with the path definition
          // So basically: /user/melissaa/dfb0d2aa/ -> /user/:username/:signatureOrSlug
          const vuePath = route.matched[0]?.path ?? UNKNOWN_PROPERTY;
          const oldPath = properties.$pathname;

          // Easiest just to string replace all instances!
          let json = JSON.stringify(properties);
          // replaceAll that typescript won't complain about...
          if (properties?.$current_url) {
            json = json.replace(
              new RegExp(properties.$current_url, 'gi'),
              properties.$current_url.replace(oldPath, vuePath)
            );
          }
          if (properties?.$pathname) {
            json = json.replace(new RegExp(properties.$pathname, 'gi'), vuePath);
          }

          properties = JSON.parse(json);
        }

        if (event === '$pageleave') {
          // FIXME: Removed pending matching with vue routes
          properties.$prev_pageview_pathname = REMOVED_PROPERTY;
        }

        // Remove initial properties
        if (!properties.$set) {
          properties.$set = {};
        }

        properties.$set.$initial_current_url = REMOVED_PROPERTY;
        properties.$set.$initial_pathname = REMOVED_PROPERTY;

        // Remove initial person url
        if (properties?.$initial_person_info?.u) {
          properties.$initial_person_info.u = REMOVED_PROPERTY;
        }

        // Clean up webvitals
        // Ref: https://github.com/PostHog/posthog-js/blob/f5a0d12603197deab305a7e25843f04f3fa4c99e/src/extensions/web-vitals/index.ts#L175
        ['LCP', 'CLS', 'FCP', 'INP'].forEach((metric) => {
          if (properties[`$web_vitals_${metric}_event`]?.$current_url) {
            properties[`$web_vitals_${metric}_event`].$current_url = REMOVED_PROPERTY;
          }
        });

        return properties;
      },
    });
    posthog.register({
      service: 'apmt',
    });

    const id = await onPageLoad();
    if (user?.authenticated) {
      posthog.identify(user.data.uniqueHash);
    } else if (id) {
      posthog.identify(id);
    }
  }
});
</script>

<template>
  <!-- Home page is actually just a redirect to another route -->
  <template v-if="routeIsHome || (routeIsLogin && isOidcAuth) || routeIsFTUE || routeIsPostLogin || routeIsLogout">
    <router-view />
  </template>

  <!-- authenticated subscriber content -->
  <template v-else-if="router.hasRoute(route.name) && (user?.authenticated || routeIsPublic)">
    <site-notification
      v-if="user?.authenticated && visibleNotification"
      :title="notificationTitle"
      :action-url="notificationActionUrl"
    >
      {{ notificationMessage }}
    </site-notification>

    <template v-if="!routeIsMaintenance">
      <!-- Desktop NavBar show / hide is handled in CSS land -->
      <nav-bar :nav-items="navItems" />

      <!-- Mobile NavBar show / hide is handled in CSS land -->
      <nav-bar-mobile />
    </template>

    <main
      :class="{
        'private-route': !routeIsPublic,
        'public-route': routeIsPublic && !routeHasModal,
        standalone: routeIsMaintenance,
      }"
    >
      <router-view />
    </main>
    <footer-bar />
  </template>
  <template v-else-if="router.hasRoute(route.name) && !routeIsPublic">
    <not-authenticated-view />
  </template>
  <template v-else-if="routeNameUndefinedDueToAbortedNav">
    <div />
  </template>
  <template v-else>
    <route-not-found-view />
  </template>
</template>

<style scoped>
@import '@/assets/styles/custom-media.pcss';

main {
  padding-block-start: 1rem;
  flex-grow: 1;

  &.private-route {
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin-inline: 1rem;
  }

  &.public-route {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
    padding-block-end: 1rem;
  }

  &.standalone {
    background: linear-gradient(to top, #ffffff, var(--colour-primary-soft) 300%);

    .dark & {
      /* TODO: No design token yet for that color */
      background: linear-gradient(to top, var(--colour-neutral-base), #24a39d 300%);
    }
  }
}

@media (--md) {
  main {
    padding-block-start: 6.5rem;
  }
}

@media (--lg) {
  main.private-route {
    margin-inline: 3.5rem;
  }
}
</style>
